import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Box,
  Checkbox,
  Flex,
  Text,
  VStack,
  Alert,
  AlertIcon,
} from "@chakra-ui/react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import {
  aaoDistributionResponseQuery,
  aaoEmpiricalDistributionQuery,
  countryPieChartQuery,
  ethnicityPieChartQuery,
  initialSignsAndSymptomsResponseQuery,
  levodopaResponseQuery,
  reporterSignsSymptomsResponseQuery,
  triggersChartQuery,
  durationChartQuery,
  treatmentResponseChartQuery,
} from "api/api-service";
import CustomSpinner from "components/CustomSpinner";
import chartDisplayConfig from "../../config/chartDisplayConfig.json";

import { parseGeneRouteParam } from "utils/utils";

const CLINICAL_FILTER_GENES = new Set(["PINK1", "PRKN"]);
const EMPTY_CLINICAL_STATUS_SELECTION = [];
const UNAFFECTED_HIDDEN_CLINICAL_CHART_IDS = new Set([
  "reporterSignsSymptomsRChart",
  "aaoDistributionChart",
  "lineChart",
  "initialSignsAndSymptomsChart",
  "levodopaResponseChart",
  "treatmentResponseChart",
]);

const hasSeriesData = (chartData) => {
  const series = chartData?.series;
  if (!Array.isArray(series)) {
    return false;
  }

  return series.some((item) => Array.isArray(item?.data) && item.data.length > 0);
};

const isChartDataAvailable = (id, data) => {
  if (!data) {
    return false;
  }

  if (Array.isArray(data)) {
    return data.some((item) => isChartDataAvailable(id, item?.chartConfig || item));
  }

  if (Array.isArray(data?.series)) {
    return hasSeriesData(data);
  }

  return true;
};

const calculateChartHeight = (chartData, id) => {
  if (chartData?.xAxis?.categories) {
    const categoryCount = chartData.xAxis.categories.length;
    if (id === "reporterSignsSymptomsRChart") {
      return `${Math.max(400, categoryCount * 60)}px`;
    }
  }
  return "400px";
};

const toggleArrayValue = (values, value, checked) => {
  if (checked) {
    return values.includes(value) ? values : [...values, value];
  }

  return values.filter((item) => item !== value);
};

const ClinicalSummaryFilterBar = ({
  filterLabel,
  affectedStatuses,
  alleleStatuses,
  onAffectedChange,
  onAlleleChange,
}) => (
  <Flex
    align="center"
    gap={6}
    wrap="wrap"
    mb={5}
    px={1}
  >
    <Text fontWeight="semibold" color="brand.700">
      {filterLabel}
    </Text>
    <Flex align="center" gap={3} wrap="wrap">
      <Text fontSize="sm" color="neutral.700">
        Clinical status
      </Text>
      <Checkbox
        isChecked={affectedStatuses.includes("affected")}
        onChange={(event) => onAffectedChange("affected", event.target.checked)}
        colorScheme="brand"
      >
        Affected
      </Checkbox>
      <Checkbox
        isChecked={affectedStatuses.includes("unaffected")}
        onChange={(event) => onAffectedChange("unaffected", event.target.checked)}
        colorScheme="brand"
      >
        Unaffected
      </Checkbox>
    </Flex>
    <Flex align="center" gap={3} wrap="wrap">
      <Text fontSize="sm" color="neutral.700">
        Allelic status
      </Text>
      <Checkbox
        isChecked={alleleStatuses.includes("monoallelic")}
        onChange={(event) => onAlleleChange("monoallelic", event.target.checked)}
        colorScheme="brand"
      >
        Monoallelic
      </Checkbox>
      <Checkbox
        isChecked={alleleStatuses.includes("biallelic")}
        onChange={(event) => onAlleleChange("biallelic", event.target.checked)}
        colorScheme="brand"
      >
        Biallelic
      </Checkbox>
    </Flex>
  </Flex>
);

const ChartWrapper = ({ id, queryFn, styles, onStatusChange = () => {} }) => {
  const { data, isLoading, error } = useQuery({
    ...queryFn,
    staleTime: Infinity, // Предотвращаем автоматическое обновление
    cacheTime: Infinity, // Храним данные в кеше
    retry: false,
  });

  const isAvailable = useMemo(
    () => isChartDataAvailable(id, data),
    [data, id]
  );

  useEffect(() => {
    if (isLoading) {
      onStatusChange(id, "loading");
      return;
    }

    onStatusChange(id, error || !isAvailable ? "hidden" : "visible");
  }, [error, id, isAvailable, isLoading, onStatusChange]);

  useEffect(() => {
    if (error) {
      console.warn(`Clinical data summary chart "${id}" is unavailable`, error);
    }
  }, [error, id]);

  // Мемоизируем опции графика
  const chartOptions = useMemo(() => {
    if (!data) return null;

    if (Array.isArray(data)) {
      return data.map(chartData => ({
        ...chartData.chartConfig,
        chart: {
          ...chartData.chartConfig.chart,
          height: calculateChartHeight(chartData.chartConfig, id),
          animation: false, // Отключаем анимацию
        },
        plotOptions: {
          ...chartData.chartConfig.plotOptions,
          series: {
            ...chartData.chartConfig.plotOptions?.series,
            animation: false, // Отключаем анимацию для серий
          }
        }
      }));
    }

    return {
      ...data,
      chart: {
        ...data.chart,
        height: calculateChartHeight(data, id),
        animation: false,
      },
      plotOptions: {
        ...data.plotOptions,
        series: {
          ...data.plotOptions?.series,
          animation: false,
        }
      }
    };
  }, [data, id]);

  if (isLoading) {
    return (
      <Box id={id} width="100%" height={styles?.height || "auto"} {...styles}>
        <CustomSpinner type="DNA" color="#0c8dc3" size={200} />
      </Box>
    );
  }

  if (error) {
    return null;
  }

  if (!isAvailable) {
    return null;
  }

  if (Array.isArray(chartOptions)) {
    return (
      <VStack
        id={id}
        spacing={8}
        align="stretch"
        width="100%"
        {...styles}
      >
        {chartOptions.map((options, index) => (
          <Box key={`${id}-${index}`} width="100%">
            <Box height={options.chart.height}>
              <HighchartsReact
                highcharts={Highcharts}
                options={options}
                updateArgs={[true, true, true]} // Принудительное обновление
              />
            </Box>
          </Box>
        ))}
      </VStack>
    );
  }

  return (
    <Box id={id} width="100%" height={chartOptions.chart.height} {...styles}>
      <HighchartsReact
        highcharts={Highcharts}
        options={chartOptions}
        updateArgs={[true, true, true]}
      />
    </Box>
  );
};

const Charts = ({ geneName, filters }) => {
  const { datasetPrefix, disease, baseDisease, gene } = parseGeneRouteParam(geneName);
  const showClinicalFilters = CLINICAL_FILTER_GENES.has(gene);
  const clinicalFilterLabel =
    datasetPrefix === "MDSGENE" ? "MDSGene filters" : "GP2 filters";
  const filterContextKey = `${datasetPrefix || "joint"}:${disease}:${gene}`;
  const [clinicalFilterState, setClinicalFilterState] = useState(() => ({
    contextKey: filterContextKey,
    affectedStatuses: [],
    alleleStatuses: [],
  }));
  const isCurrentFilterContext =
    clinicalFilterState.contextKey === filterContextKey;
  const affectedStatuses = isCurrentFilterContext
    ? clinicalFilterState.affectedStatuses
    : EMPTY_CLINICAL_STATUS_SELECTION;
  const effectiveAffectedStatuses =
    affectedStatuses.includes("affected") &&
    affectedStatuses.includes("unaffected")
      ? EMPTY_CLINICAL_STATUS_SELECTION
      : affectedStatuses;
  const alleleStatuses = isCurrentFilterContext
    ? clinicalFilterState.alleleStatuses
    : EMPTY_CLINICAL_STATUS_SELECTION;

  useEffect(() => {
    setClinicalFilterState((previous) =>
      previous.contextKey === filterContextKey
        ? previous
        : {
            contextKey: filterContextKey,
            affectedStatuses: [],
            alleleStatuses: [],
          }
    );
  }, [filterContextKey]);

  const handleAffectedChange = useCallback((value, checked) => {
    setClinicalFilterState((previous) => ({
      contextKey: filterContextKey,
      affectedStatuses: toggleArrayValue(
        previous.contextKey === filterContextKey
          ? previous.affectedStatuses
          : [],
        value,
        checked
      ),
      alleleStatuses:
        previous.contextKey === filterContextKey
          ? previous.alleleStatuses
          : [],
    }));
  }, [filterContextKey]);

  const handleAlleleChange = useCallback((value, checked) => {
    setClinicalFilterState((previous) => ({
      contextKey: filterContextKey,
      affectedStatuses:
        previous.contextKey === filterContextKey
          ? previous.affectedStatuses
          : [],
      alleleStatuses: toggleArrayValue(
        previous.contextKey === filterContextKey ? previous.alleleStatuses : [],
        value,
        checked
      ),
    }));
  }, [filterContextKey]);

  const chartFilters = useMemo(
    () => ({
      ...filters,
      affectedStatuses: showClinicalFilters ? effectiveAffectedStatuses : [],
      alleleStatuses: showClinicalFilters ? alleleStatuses : [],
    }),
    [alleleStatuses, effectiveAffectedStatuses, filters, showClinicalFilters]
  );

  // List of genes that should have all charts
  const specialGenes = ["CACNA1A", "KCNA1", "PDHA1", "SLC1A3"];
  const isSpecialGene = specialGenes.includes(gene);

  const chartConfigs = useMemo(() => {
    const chartFactories = {
      reporterSignsSymptomsRChart: () => ({
        id: "reporterSignsSymptomsRChart",
        queryFn: reporterSignsSymptomsResponseQuery(disease, gene, chartFilters),
        styles: { width: "100%" },
      }),
      aaoDistributionChart: () => ({
        id: "aaoDistributionChart",
        queryFn: aaoDistributionResponseQuery(disease, gene, chartFilters),
      }),
      lineChart: () => ({
        id: "lineChart",
        queryFn: aaoEmpiricalDistributionQuery(disease, gene, chartFilters),
      }),
      initialSignsAndSymptomsChart: () => ({
        id: "initialSignsAndSymptomsChart",
        queryFn: initialSignsAndSymptomsResponseQuery(disease, gene, chartFilters),
      }),
      ethnicityPieChart: () => ({
        id: "ethnicityPieChart",
        queryFn: ethnicityPieChartQuery(disease, gene, chartFilters),
      }),
      levodopaResponseChart: () => ({
        id: "levodopaResponseChart",
        queryFn: levodopaResponseQuery(disease, gene, chartFilters),
      }),
      countryPieChart: () => ({
        id: "countryPieChart",
        queryFn: countryPieChartQuery(disease, gene, chartFilters),
      }),
      triggersChart: () => ({
        id: "triggersChart",
        queryFn: triggersChartQuery(disease, gene, chartFilters),
      }),
      durationChart: () => ({
        id: "durationChart",
        queryFn: durationChartQuery(disease, gene, chartFilters),
      }),
      treatmentResponseChart: () => ({
        id: "treatmentResponseChart",
        queryFn: treatmentResponseChartQuery(disease, gene, chartFilters),
      }),
    };

    const defaultOrder = [
      "reporterSignsSymptomsRChart",
      "aaoDistributionChart",
      "lineChart",
      "initialSignsAndSymptomsChart",
      "ethnicityPieChart",
      "levodopaResponseChart",
      "countryPieChart",
      "triggersChart",
      "durationChart",
      "treatmentResponseChart",
    ];

    const specialOnlyIds = new Set([
      "triggersChart",
      "durationChart",
      "treatmentResponseChart",
    ]);

    const allowedIds =
      chartDisplayConfig?.[disease]?.[gene] ||
      chartDisplayConfig?.[baseDisease]?.[gene];

    const hideClinicalCharts = chartFilters.affectedStatuses.includes(
      "unaffected"
    );
    const idsToRender = (allowedIds && allowedIds.length
      ? allowedIds
      : defaultOrder.filter((id) => !specialOnlyIds.has(id) || isSpecialGene)
    ).filter(
      (id) =>
        !(
          hideClinicalCharts &&
          UNAFFECTED_HIDDEN_CLINICAL_CHART_IDS.has(id)
        )
    );

    return idsToRender
      .map((id) => {
        const factory = chartFactories[id];
        return factory ? factory() : null;
      })
      .filter(Boolean);
  }, [baseDisease, chartFilters, disease, gene, isSpecialGene]);

  const chartIds = useMemo(
    () => chartConfigs.map(({ id }) => id),
    [chartConfigs]
  );
  const [chartStatuses, setChartStatuses] = useState({});
  const handleChartStatusChange = useCallback((id, status) => {
    setChartStatuses((previous) =>
      previous[id] === status ? previous : { ...previous, [id]: status }
    );
  }, []);

  useEffect(() => {
    setChartStatuses({});
  }, [chartIds, filters, geneName]);

  const visibleChartCount = chartIds.filter(
    (id) => chartStatuses[id] === "visible"
  ).length;
  const resolvedChartCount = chartIds.filter((id) =>
    ["visible", "hidden"].includes(chartStatuses[id])
  ).length;
  const showNoChartsMessage =
    chartIds.length > 0 &&
    resolvedChartCount === chartIds.length &&
    visibleChartCount === 0;

  if (!chartConfigs.length) {
    return (
      <Alert status="info">
        <AlertIcon />
        No clinical data summaries available
      </Alert>
    );
  }

  return (
    <Box maxW="100%" mx="auto" p={5}>
      {showClinicalFilters && (
        <ClinicalSummaryFilterBar
          filterLabel={clinicalFilterLabel}
          affectedStatuses={affectedStatuses}
          alleleStatuses={alleleStatuses}
          onAffectedChange={handleAffectedChange}
          onAlleleChange={handleAlleleChange}
        />
      )}
      {showNoChartsMessage && (
        <Alert status="info" mb={4}>
          <AlertIcon />
          No clinical data summaries available
        </Alert>
      )}
      {chartConfigs.length === 1 ? (
        <ChartWrapper
          {...chartConfigs[0]}
          onStatusChange={handleChartStatusChange}
        />
      ) : (
        <Box
          data-testid="chart-masonry"
          data-layout-mode="independent-columns"
          sx={{
            columnCount: { base: 1, md: 2 },
            columnGap: "var(--chakra-space-6)",
          }}
        >
          {chartConfigs.map(({ id, queryFn }) => (
            <ChartWrapper
              key={id}
              id={id}
              queryFn={queryFn}
              styles={{
                width: "100%",
                mb: 6,
                "data-chart-layout-item": "true",
                sx: {
                  breakInside: "avoid",
                  WebkitColumnBreakInside: "avoid",
                },
              }}
              onStatusChange={handleChartStatusChange}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default Charts;
